import {
  Button,
  LoadingIndicator,
  SlideoutMenu,
  Tabs,
} from "@light-technology/components";
import { Check, Link01 } from "@untitledui/icons";
import { type ReactNode, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { api } from "../../state/api";
import { type AppResponse } from "../../state/api.types";
import { plansLink } from "../../utilities/links";
import { DevicesCount } from "./DevicesCount";
import { LinkedPlans } from "./LinkedPlans";
import { ProgramDesign } from "./ProgramDesign";

type ProgramSlideoutProps = {
  app: AppResponse;
  children: ReactNode;
};

const tabs = [
  { id: "details", label: "Details" },
  { id: "linked-plans", label: "Linked plans" },
];

const ProgramOverview = ({
  app,
  programId,
  close,
}: {
  app: AppResponse;
  programId: string;
  close: () => void;
}) => {
  const [tab, setTab] = useState<string | number>(tabs[0].id);
  const getProgram = api.useGetProgramQuery(programId);
  const getPlans = api.useGetAppPlansQuery(app.uuid);

  const program = getProgram.data;

  return (
    <Tabs selectedKey={tab} onSelectionChange={setTab}>
      <SlideoutMenu.Header onClose={close} tabs={tabs}>
        {!program ? (
          <LoadingIndicator type="brand-spinner" />
        ) : (
          <>
            <div className="flex flex-row items-center gap-2">
              <div className="text-display-xs font-semibold">
                {program.name} Program
              </div>
              <Button
                size="sm"
                color="tertiary"
                tooltip="Copy shareable link to program"
                tooltipOnClick="Link copied to clipboard"
                iconLeading={Link01}
                iconLeadingOnClick={Check}
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                }}
              />
            </div>
            <div className="font-mono text-sm text-tertiary">
              {program.uuid}
            </div>
          </>
        )}
      </SlideoutMenu.Header>
      {program && (
        <SlideoutMenu.Content className="h-full pt-3xl">
          <Tabs.Panel id="details" className="h-full">
            <div className="flex flex-col gap-3xl">
              <DevicesCount program={program} />
              <ProgramDesign program={program} />
            </div>
          </Tabs.Panel>
          <Tabs.Panel id="linked-plans" className="h-full">
            <LinkedPlans
              app={app}
              linkedPlanUUIDs={program.linked_plans}
              plans={getPlans.data?.data ?? []}
            />
          </Tabs.Panel>
        </SlideoutMenu.Content>
      )}
    </Tabs>
  );
};

export const ProgramSlideout = ({ app, children }: ProgramSlideoutProps) => {
  const navigate = useNavigate();
  const { programId } = useParams();

  const close = () => {
    navigate(plansLink(app.slug));
  };

  return (
    <SlideoutMenu.Trigger
      isOpen={!!programId}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          close();
        }
      }}
    >
      {children}
      <SlideoutMenu isDismissable className="max-w-[560px]">
        {programId && (
          <ProgramOverview app={app} programId={programId} close={close} />
        )}
      </SlideoutMenu>
    </SlideoutMenu.Trigger>
  );
};
